import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { Observable, catchError, finalize, of, shareReplay, switchMap, tap } from 'rxjs';
import { hashText } from '../../../auth/access-token-session.util';
import { AuthService } from '../../../auth/auth.service';
import { SvcDataCacheService } from '../../../services/cache/svc-data-cache.service';
import { SvcCacheName } from '../../../services/cache/svc-cache-name.enum';
import { LOGIN_SCOPE_TERMS } from '../../transloco/login-scope.terms';

export interface LanguageTerm {
  tn: string;
  tv: string;
}

interface CachedLoginScopeTerms {
  languageId: number;
  scopeHash: number;
  terms: LanguageTerm[];
}

const LOGIN_SCOPE_ROUTES = [
  '/sign-in',
  '/global-sign-in',
  '/forgot-password',
  '/reset-password',
  '/user-locked',
  '/page-cant-be-load',
  '/sign-out',
  '/expired-password',
  '/multi-signin',
];

const LOGIN_SCOPE_CACHE_EXPIRATION = { hours: 24 };

const LOGIN_SCOPE_HASH = hashText(LOGIN_SCOPE_TERMS.join('\n'));

/**
 * Dictionary requests made without an authenticated session.
 * Screens shown before a user and plant are set download only {@link LOGIN_SCOPE_TERMS};
 * any other anonymous screen, or a failed or empty scoped response, falls back to the full dictionary.
 */
@Injectable({
  providedIn: 'root',
})
export class AnonymousTermsService {
  readonly #httpClient = inject(HttpClient);
  readonly #appConfig = inject(AppEnvironmentConfig);
  readonly #authService = inject(AuthService);
  readonly #svcDataCacheService = inject(SvcDataCacheService);
  readonly #pendingRequests = new Map<string, Observable<LanguageTerm[]>>();

  getTerms(languageId: number): Observable<LanguageTerm[]> {
    return this.#isLoginScope() ? this.#getLoginScopeTerms(languageId) : this.#share(`all-${languageId}`, this.#getAllTerms(languageId));
  }

  // AB#74985 - without any token AuthGuard sends every route to sign-in; an expired token on an internal route keeps the full dictionary (AB#74984).
  #isLoginScope(): boolean {
    const path = globalThis.location.pathname;
    return !this.#authService.accessToken || LOGIN_SCOPE_ROUTES.some((route) => path.includes(route));
  }

  #getLoginScopeTerms(languageId: number): Observable<LanguageTerm[]> {
    const cached = this.#svcDataCacheService.get<CachedLoginScopeTerms>(SvcCacheName.ANONYMOUS_TERMS);
    if (cached?.languageId === languageId && cached.scopeHash === LOGIN_SCOPE_HASH && cached.terms?.length) {
      return of(cached.terms);
    }
    const request$ = this.#httpClient
      .post<LanguageTerm[]>(`${this.#appConfig.APIs.apiUrlAuth}/Languages/${languageId}/terms/search`, { termKeys: LOGIN_SCOPE_TERMS })
      .pipe(
        catchError(() => of(null)),
        tap((terms) => {
          if (terms?.length) {
            this.#svcDataCacheService.set<CachedLoginScopeTerms>(
              SvcCacheName.ANONYMOUS_TERMS,
              { languageId, scopeHash: LOGIN_SCOPE_HASH, terms },
              LOGIN_SCOPE_CACHE_EXPIRATION
            );
          }
        }),
        switchMap((terms) => (terms?.length ? of(terms) : this.#getAllTerms(languageId)))
      );
    return this.#share(`login-${languageId}`, request$);
  }

  #getAllTerms(languageId: number): Observable<LanguageTerm[]> {
    return this.#httpClient.get<LanguageTerm[]>(`${this.#appConfig.APIs.apiUrlAuth}/Languages/terms/v2/${languageId}`);
  }

  #share(key: string, request$: Observable<LanguageTerm[]>): Observable<LanguageTerm[]> {
    const pending = this.#pendingRequests.get(key);
    if (pending) return pending;
    const shared$ = request$.pipe(
      finalize(() => this.#pendingRequests.delete(key)),
      shareReplay(1)
    );
    this.#pendingRequests.set(key, shared$);
    return shared$;
  }
}
